import { Projeto } from '../tipos/projeto'

interface KanbanCardProps {
  projeto: Projeto
  onClick: (projeto: Projeto) => void
  isDragging?: boolean
  isReadOnly?: boolean
}

// Função para obter iniciais do nome
function getInitials(nome: string): string {
  if (!nome) return '?'
  const parts = nome.trim().split(' ')
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Função para gerar cor baseada no nome (hash simples)
function getColorFromName(nome: string): { bg: string; text: string } {
  if (!nome) {
    return { bg: 'bg-gray-100', text: 'text-gray-700' }
  }

  // Lista de cores variadas e harmoniosas
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-purple-100', text: 'text-purple-700' },
    { bg: 'bg-pink-100', text: 'text-pink-700' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700' },
    { bg: 'bg-teal-100', text: 'text-teal-700' },
    { bg: 'bg-green-100', text: 'text-green-700' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },
    { bg: 'bg-red-100', text: 'text-red-700' },
    { bg: 'bg-rose-100', text: 'text-rose-700' },
    { bg: 'bg-violet-100', text: 'text-violet-700' },
  ]

  // Gerar hash simples do nome
  let hash = 0
  for (let i = 0; i < nome.length; i++) {
    hash = nome.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Usar valor absoluto e módulo para selecionar cor
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

// Cores específicas para cada status (mesmas do KanbanColumn)
const STATUS_COLORS: Record<number, { border: string; badge: string }> = {
  1: { border: 'border-gray-300', badge: 'bg-gray-100 text-gray-700' }, // Backlog
  2: { border: 'border-blue-300', badge: 'bg-blue-100 text-blue-700' }, // Escopo
  3: { border: 'border-purple-300', badge: 'bg-purple-100 text-purple-700' }, // Desenvolvimento
  4: { border: 'border-yellow-300', badge: 'bg-yellow-100 text-yellow-700' }, // Testes Internos
  5: { border: 'border-orange-300', badge: 'bg-orange-100 text-orange-700' }, // Testes de Produto
  6: { border: 'border-indigo-300', badge: 'bg-indigo-100 text-indigo-700' }, // Homologação
  7: { border: 'border-pink-300', badge: 'bg-pink-100 text-pink-700' }, // Pausado
  8: { border: 'border-red-300', badge: 'bg-red-100 text-red-700' }, // Cancelado
  9: { border: 'border-amber-300', badge: 'bg-amber-100 text-amber-700' }, // Aguardando Terceiros
  10: { border: 'border-cyan-300', badge: 'bg-cyan-100 text-cyan-700' }, // Pré Produção
  11: { border: 'border-green-300', badge: 'bg-green-100 text-green-700' }, // Produção
}

export function KanbanCard({ projeto, onClick, isDragging = false, isReadOnly = false }: KanbanCardProps) {
  const style = {
    opacity: isDragging ? 0.5 : 1,
    cursor: isReadOnly ? 'default' : 'pointer',
  }

  const analistaNome = (projeto as any).analistaSMNNome || 'Não definido'
  const analistaIniciais = getInitials(analistaNome)
  const analistaColor = getColorFromName(analistaNome)
  const statusCodigo = Number(projeto.statusCodigo)
  const statusColor = STATUS_COLORS[statusCodigo] || { border: 'border-gray-300', badge: 'bg-gray-100 text-gray-700' }

  return (
    <div
      style={style}
      onClick={() => onClick(projeto)}
      className={`bg-white rounded-md shadow-sm border ${statusColor.border} p-3 hover:shadow-md hover:border-opacity-60 transition-all duration-150 ${
        isReadOnly ? 'cursor-pointer' : 'cursor-pointer'
      } ${isDragging ? 'ring-2 ring-brand-500 shadow-lg scale-105' : ''}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(projeto)
        }
      }}
    >
      {/* Título do Projeto */}
      <div className="mb-2">
        <h4 className="font-medium text-gray-900 text-sm leading-5 line-clamp-2">
          {projeto.nome}
        </h4>
      </div>

      {/* Informações do Card */}
      <div className="flex items-center justify-between gap-2 mt-3">
        {/* Ticket */}
        {projeto.numeroTicket && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
              #{projeto.numeroTicket}
            </span>
          </div>
        )}

        {/* Avatar do Analista */}
        <div className="flex items-center gap-1.5 ml-auto">
          <div
            className={`w-6 h-6 rounded-full ${analistaColor.bg} ${analistaColor.text} text-xs font-medium flex items-center justify-center flex-shrink-0`}
            title={analistaNome}
          >
            {analistaIniciais}
          </div>
        </div>
      </div>

      {/* Indicador de Status com cor específica */}
      {projeto.statusNome && (
        <div className="mt-2">
          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${statusColor.badge}`}>
            {projeto.statusNome}
          </span>
        </div>
      )}
    </div>
  )
}
